import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';

export interface WalletTransaction {
  id: string;
  type: string;
  tokens: number;
  amount: number;
  source: string;
  created_at: Date | string;
  metadata?: any;
  payment_transaction_id?: string;
  payment_status?: string;
  payment_transaction_date?: Date | string;
  updated_at?: Date | string;
}

export interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

interface UseWalletTransactionsApiReturn {
  transactions: WalletTransaction[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  loadPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useWalletTransactionsApi = (enabled = true): UseWalletTransactionsApiReturn => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTransactions = async (page: number = 1, limit: number = 10) => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get Firebase ID token
      const idToken = await user.getIdToken();
      
      const url = new URL(API_ENDPOINTS.WALLET_TRANSACTIONS);
      url.searchParams.set('page', page.toString());
      url.searchParams.set('limit', limit.toString());

      console.log('Fetching wallet transactions from API:', url.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response:', data);

      if (data.success) {
        // Convert date strings to Date objects
        const processedTransactions = data.data.transactions.map((transaction: any) => ({
          ...transaction,
          created_at: transaction.created_at ? new Date(transaction.created_at) : null,
          payment_transaction_date: transaction.payment_transaction_date ? new Date(transaction.payment_transaction_date) : null,
          updated_at: transaction.updated_at ? new Date(transaction.updated_at) : null,
        }));

        setTransactions(processedTransactions);
        setPagination(data.data.pagination);
        setCurrentPage(page);
      } else {
        throw new Error(data.message || 'Failed to fetch transactions');
      }
    } catch (err) {
      console.error('Error fetching wallet transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (page: number) => {
    await fetchTransactions(page);
  };

  const refresh = async () => {
    await fetchTransactions(currentPage);
  };

  useEffect(() => {
    if (enabled) {
      fetchTransactions();
    }
  }, [enabled, user]);

  return {
    transactions,
    pagination,
    loading,
    error,
    loadPage,
    refresh,
  };
};
